import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Editdispatchheader from "./editdispatchheader";
import Editdispatchform from "./editdispatchform";
import CancelButton from "../common/ui/cancelbutton";
import Button from "../common/ui/button";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import WarningModal from "../common/ui/warningmodel";

export default function EditDispatch() {
    const navigate = useNavigate();

    const [entries, setEntries] = useState([]);
    const [binumber, setBINumber] = useState([]);
    const [loadingBINumber, setLoadingBINumber] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [warningVisible, setWarningVisible] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");

    const [editEntry, setEditEntry] = useState(null);

    const logout = useAuth();

    const location = useLocation();
    const { parentId, childId } = location.state || {};
    const isEditMode = parentId != null && childId != null;



    const fetchBINumber = useCallback(async () => {
        try {
            setLoadingBINumber(true);
            let response = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_BI_NUMBER}`,
                { method: "GET", headers: getHeaders(true) }
            );

            // -------------------------
            // 🔥 If ACCESS TOKEN expired
            // -------------------------
            if (response.status === 401) {
                console.warn("Access token expired. Attempting refresh...");

                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry API call with new token
                    response = await fetch(
                        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_BI_NUMBER}`,
                        { method: "GET", headers: getHeaders(true) }
                    );
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout(); // redirect user
                    return;
                }
            }


            const result = await response.json();
            if (result.status === 200 && Array.isArray(result.data)) {
                setBINumber(result.data.map((item) => ({
                    label: item.binumber.toUpperCase(),
                    value: item.id,
                    binumber: item.binumber,
                })));
            } else setBINumber([]);
        } catch (error) {
            setBINumber([]);
        } finally {
            setLoadingBINumber(false);
        }
    }, [logout]);



    // FETCH: DISPATCH EDIT DATA
    const fetchEditDetails = useCallback(async () => {
        if (!parentId) return;

        try {
            const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_EDIT_DETAILS(parentId, childId ?? "")}`;
            const callApi = async () => await fetch(url, { method: "GET", headers: getHeaders(true) });

            // Initial API call
            let res = await callApi();

            // -------------------------
            // 🔥 Handle 401 token expired
            // -------------------------
            if (res.status === 401) {
                console.warn("Access token expired. Attempting refresh...");
                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry the same API call
                    res = await callApi();
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout();
                    return;
                }
            }
            const result = await res.json();

            if (result.status === 200 && result.data) {
                const { master_data, child_data } = result.data;

                const filteredChildren = childId
                    ? (child_data || []).filter(c => c.exbond_child_id === childId || c.id === childId)
                    : child_data || [];

                const tableEntries = filteredChildren.map(child => ({
                    id: child?.id || null,
                    dispatch_master_id: master_data?.id || null,
                    dispatch_id: master_data?.dispatch_id || "",
                    total_dispatch_weight: master_data?.total_dispatch_weight || "",

                    itemId: child?.exbond_child_id ?? child?.id,
                    exbondNo: child?.exbond_id,

                    dispatch_child_id: child?.id || null,
                    material_master_id: child?.material_master_id,
                    material: child?.material_name,
                    weight: child?.exbond_weight,
                    customer: child?.customer_name,
                    dispatchDate: child?.dispatch_date,
                    dispatchWeight: child?.dispatch_weight,
                    truckNo: child?.truck_number,
                    binumber: child?.inbond_bi_number,
                }));




                setEntries(tableEntries);

                if (isEditMode) {
                    const editChild = tableEntries.find(e => e.itemId === childId || e.dispatch_child_id === childId);
                    setEditEntry(editChild || null);
                }
            }
        } catch (error) {
            console.error("Error fetching edit details:", error);
        }
    }, [parentId, childId, isEditMode, logout]);

    useEffect(() => {
        fetchBINumber();
        fetchEditDetails();
    }, [parentId, childId, fetchBINumber, fetchEditDetails]);

    // WHEN CLICKING EDIT ICON

    const handleEditEntry = (entry) => {


        setEditEntry(entry);
    };

    // WHEN FORM SUBMITS UPDATED ITEM
    const handleUpdateEntry = (updatedEntry) => {
        setEntries(prev =>
            prev.map(e =>
                e.itemId === updatedEntry.itemId ? { ...e, ...updatedEntry } : e
            )
        );
        setEditEntry(null);
        console.log("handleEditEntry", updatedEntry);
    };


    // ADD NEW ENTRY
    const handleAddEntry = (newEntry) => {
        setEntries(prev => [...prev, newEntry]);
    };

    // DELETE ENTRY
    const handleDeleteEntry = (id) => {
        setEntries(prev => prev.filter(e => e.itemId !== id));
    };



    const canSubmit = entries.length > 0 && !submitting;

    const handleSubmit = async () => {
        if (entries.length === 0) {
            setWarningMessage("Please add at least one entry before submitting.");
            setWarningVisible(true);
            return;
        }



        console.log("entries", entries);


        // Payload for API
        const payload = {
            total_dispatch_weight: entries[0]?.totalWeightByDispachedMasterId,
            dispatchchild: entries.map(entry => {
                return {
                    dispatch_child_id: entry.dispatch_child_id || null,   // <-- FIXED (backend expects this)
                    exbond_child_id: entry.itemId,         // always required
                    dispatch_date: entry.dispatchDate,
                    dispatch_weight: Number(entry.dispatchWeight),
                    truck_no: entry.truckNo
                };
            })

        };

        console.log("payload", payload);



        setSubmitting(true);

        try {
            let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.UPDATE_COMPLETE((parentId))}`;
            let method = "PUT";
            const callApi = async () =>
                await fetch(url, {
                    method,
                    headers: getHeaders(true),
                    body: JSON.stringify(payload),
                });

            // Initial API call
            let response = await callApi();

            // -------------------------
            // 🔥 Handle 401 token expired
            // -------------------------
            if (response.status === 401) {
                console.warn("Access token expired. Attempting refresh...");
                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry the same API call
                    response = await callApi();
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout();
                    return;
                }
            }

            const result = await response.json();

            if (result.status === 201 || result.status === 200) {
                setEntries([]);
                navigate("/dispatch", {
                    state: {
                        showToast: true,
                        message: result.message || (isEditMode ? "Dispatch updated successfully!" : "Dispatch created successfully!"),
                    },
                });
            } else {
                setWarningMessage(result.message || "Failed to submit dispatch entry.");
                setWarningVisible(true);
            }
        } catch (error) {
            setWarningMessage("Something went wrong while submitting dispatch.");
            setWarningVisible(true);
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="relative mx-auto w-full px-3 sm:px-4 md:px-6">
                <Editdispatchheader />
                <Editdispatchform
                    onAddEntry={handleAddEntry}
                    onEditEntry={handleEditEntry}
                    onDeleteEntry={handleDeleteEntry}
                    onUpdateEntry={handleUpdateEntry}
                    biNumberOptions={binumber}
                    loadingBINumber={loadingBINumber}
                    editEntry={editEntry}
                    entries={entries}
                    selectedCildId={childId}


                />
                <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6">
                    <CancelButton type="button" className="w-full sm:w-auto" onClick={() => navigate("/dispatch")}>
                        Cancel
                    </CancelButton>
                    <Button onClick={handleSubmit} type="button" className="w-full sm:w-auto order-1 sm:order-2" disabled={!canSubmit}>
                        Submit
                    </Button>
                </div>
            </div>
            {warningVisible && (
                <WarningModal
                    message={warningMessage}
                    onClose={() => setWarningVisible(false)}
                />
            )}
        </>
    );
}
